package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.ui.api.AuthApi
import com.advaita.cloud.drive.ui.api.CreateTarget
import com.advaita.cloud.drive.ui.store.FileBrowserStore
import com.advaita.cloud.drive.ui.store.MyAccessStore
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.files.get

/**
 * The top bar. [held] (ADV-1326c): the person is on the Pending or no-access page, so the bar
 * shows only who is signed in and Sign out. New folder, Upload files, Upload folder, Pull from
 * URL and Run workflow would each answer `403`.
 */
@Composable
fun Header(store: FileBrowserStore, held: Boolean = false, access: MyAccessStore? = null) {
    Div(attrs = { classes("header") }) {
        H1 { Text("📁 Advaita Data Hub") }
        val user = store.currentUser
        if (user != null) {
            Span(attrs = {
                style { property("color", "#5f6368"); property("font-size", "14px"); property("margin-right", "8px") }
            }) {
                Text("Signed in as ${user.displayName}")
            }
        }
        if (!held) CreateActions(store)
        // CR-6.5: the Account menu (My access, Organization console). Sign out stays where it is.
        if (!held && access != null && store.shareUiVisible) AccountMenu(access)
        Button(attrs = {
            classes("btn")
            attr("data-tip", "Sign out")
            onClick {
                MainScope().launch {
                    signOut(
                        logoutUrl = store.authConfig?.az2LogoutUrl,
                        clearResume = { store.resumableStore?.clearAll() },
                        clearToken = AuthApi::clearToken,
                        navigate = { window.location.href = it },
                    )
                }
            }
        }) {
            Text("Sign out")
        }
    }
}

/**
 * The header's buttons that create something. [Header] leaves them out for a held person.
 *
 * ADV-1330 (the shared-folder create slice): each button targets [FileBrowserStore.createTarget],
 * and [FileBrowserStore.createActions] says whether it works there and what its tip says. In My
 * Drive they add to the current folder, as before. In Shared with me they add to the open shared
 * folder when the caller's grant allows it, and they are off at its top level (F3 kept a new file
 * from landing where the user is not looking, and this still holds).
 */
@Composable
private fun CreateActions(store: FileBrowserStore) {
    val actions = store.createActions
    Button(attrs = {
        classes("btn")
        attr("data-tip", actions.newFolder.tip)
        if (!actions.newFolder.enabled) attr("disabled", "")
        onClick {
            if (!actions.newFolder.enabled) return@onClick
            // ADV-1330: pinned at the click, before the prompt, as Pull and Run workflow are.
            val target = store.createTarget ?: return@onClick
            MainScope().launch {
                val name = store.prompt(
                    title = "New folder",
                    inputLabel = "Folder name",
                    confirmLabel = "Create",
                )
                if (!name.isNullOrBlank()) store.createFolder(name, target)
            }
        }
    }) {
        Text("New folder")
    }
    // Disable upload buttons until /api/config resolves so an early click
    // can't fall through to the wrong endpoint (uploadAll branches on
    // authConfig.storageBackend; null would route to the legacy local-fs
    // path even when the deployed backend is S3).
    val configReady = store.authConfig != null
    val filesReady = configReady && actions.uploadFiles.enabled
    Button(attrs = {
        classes("btn")
        attr("data-tip", actions.uploadFiles.tip)
        if (!filesReady) attr("disabled", "")
        onClick {
            // ADV-1330: pinned at the click, before the picker opens, as New folder is.
            val target = store.createTarget
            if (filesReady && target != null) openFilePicker(store, target, allowMultiple = true, asFolder = false)
        }
    }) {
        Text("Upload files")
    }
    val folderReady = configReady && actions.uploadFolder.enabled
    Button(attrs = {
        classes("btn", "primary")
        attr("data-tip", actions.uploadFolder.tip)
        if (!folderReady) attr("disabled", "")
        onClick {
            val target = store.createTarget
            if (folderReady && target != null) openFilePicker(store, target, allowMultiple = true, asFolder = true)
        }
    }) {
        Text("Upload folder")
    }
    // "Pull from URL" — download public http/https files straight into the current folder.
    // Flag-gated: only shown when the backend reports the pull feature is enabled.
    if (store.authConfig?.pullEnabled == true) {
        val submitting = store.pullSubmitting
        val pullReady = !submitting && actions.pull.enabled
        Button(attrs = {
            classes("btn")
            attr("data-tip", actions.pull.tip)
            if (!pullReady) attr("disabled", "")
            onClick { if (pullReady) store.pullFromUrls() }
        }) {
            if (submitting) {
                Span(attrs = { classes("spinner") }) { Icon(IconName.REFRESH) }
                Text(" Starting…")
            } else {
                Text("Pull from URL")
            }
        }
    }
    // ADV-669 — the OPC run wizard: choose an opinionated use-case, pick files against
    // its declared file-path/file-set inputs (D4), and submit.
    // The busy state is not decoration. wispResponseTimeout is 10s, so when the
    // registry is unreachable this click used to sit there looking idle for ten
    // seconds before saying anything — long enough that the honest reading was "the
    // button doesn't work". Disabled + a wait cursor + changed label says "heard you,
    // waiting on something", which is the difference between slow and broken.
    val catalogLoading = store.opcCatalogLoading
    val runReady = !catalogLoading && actions.runWorkflow.enabled
    Button(attrs = {
        if (catalogLoading) classes("btn", "busy") else classes("btn")
        attr("data-tip", actions.runWorkflow.tip)
        if (!runReady) attr("disabled", "")
        onClick { if (runReady) store.runOpc() }
    }) {
        Text(if (catalogLoading) "Loading…" else "Run workflow")
    }
}

/**
 * Sign out, from every page: the header's Sign out, the Pending and no-access hold pages included.
 *
 * It wipes the per-user resume metadata first, so a different person on this browser profile
 * can't inherit it, and waits for the wipe because the navigation below unloads the page. The wait
 * stops after [resumeClearTimeoutMs]: a blocked IndexedDB must not keep the person signed in. Then it
 * clears the DataHub token and sends the WHOLE tab to [logoutUrl], az2's
 * `GET /logout?redirect_uri=<DataHub>/ui/` page. Its Sign Out button posts to az2's `POST /logout`,
 * which ends the az2 session. az2 has no `logoutSuccessUrl`, so the person lands on az2's
 * `<authorize base>/login?logout`, not back on DataHub: `redirect_uri` only names DataHub on the
 * confirm page. With no [logoutUrl], or one that does not start with `https://` or `http://`, the
 * tab goes to `/ui/`: the server checks the URL at startup, and this guard keeps a `javascript:`
 * URL out of `location.href` all the same.
 *
 * Without the az2 step, az2's session survives and Sign in signs the same person in again:
 * autoapprove=true on the OAuth client skips consent.
 *
 * ADV-1411: this was a `no-cors` background fetch to `<baseUrl>/sso/logout`. az2 has no such
 * route (it answered 302 to `/login` and ended nothing), and `baseUrl` is the back-channel host,
 * not the browser's.
 */
internal suspend fun signOut(
    logoutUrl: String?,
    clearResume: suspend () -> Unit,
    clearToken: () -> Unit,
    navigate: (String) -> Unit,
    resumeClearTimeoutMs: Long = 1_000L,
) {
    runCatching { withTimeoutOrNull(resumeClearTimeoutMs) { clearResume() } }
    clearToken()
    val az2 = logoutUrl?.trim()?.takeIf { it.startsWith("https://", ignoreCase = true) || it.startsWith("http://", ignoreCase = true) }
    navigate(az2 ?: "/ui/")
}

private fun openFilePicker(
    store: FileBrowserStore,
    target: CreateTarget,
    allowMultiple: Boolean = false,
    asFolder: Boolean = false,
) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.style.display = "none"
    if (allowMultiple) input.multiple = true
    if (asFolder) {
        // Non-standard but widely supported (Chrome, Firefox, Edge, Safari).
        input.setAttribute("webkitdirectory", "")
        input.setAttribute("directory", "")
    }
    input.onchange = {
        val list = input.files
        if (list != null && list.length > 0) {
            val items = mutableListOf<FileBrowserStore.FileToUpload>()
            for (i in 0 until list.length) {
                val f = list[i] ?: continue
                // webkitRelativePath is an untyped extension on File. For folder
                // uploads the browser sets this; for plain file picks it's empty.
                val rel = (f.asDynamic().webkitRelativePath as? String)?.takeIf { it.isNotBlank() }
                items += FileBrowserStore.FileToUpload(f, rel)
            }
            store.uploadAll(items, target)
        }
        input.remove()
        Unit
    }
    document.body?.appendChild(input)
    input.click()
}
